import { Component, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { concatMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ActivateFormValue } from '../../models/activate-link.model';
import { CouponRequest, CouponResponse, DISCOUNT_TYPE, DURATION_TYPE, PAYMENT_METHODS_MAP, Plan, TIME_UNITS } from 'src/app/domains/sales/activation-link/models/activation-link.model';
import { environment } from 'src/environments/environment';
import { CopyFeedbackPopupComponent } from 'src/app/shared/components';
import { ActivationLinkService } from 'src/app/domains/sales/activation-link/services';
import { calculateExpiryDate } from 'src/app/domains/sales/activation-link/utils/activation-link-utils';

@Component({
  selector: 'gdp-activation-link-plans',
  templateUrl: './activation-link-plans.component.html',
  styleUrls: ['./activation-link-plans.component.scss']
})
export class ActivationLinkPlansComponent {
  public modalContent = '';
  public loading: boolean = false;
  public activationFormControl = new FormControl<ActivateFormValue>(null);

  @ViewChild('activationPlansLinkPopup', { static: false }) activationPlansLinkPopup: CopyFeedbackPopupComponent;

  constructor(
    private modalService: NgbModal,
    private datePipe: DatePipe,
    private activationLinkService: ActivationLinkService
  ) { }

  public onSubmit(): void {
    if (this.activationFormControl.valid) {
      const value = this.activationFormControl.value;
      if (value.discount) {
        this.createNewCoupon(value);
      } else {
        this.createLink(value);
      }
    }
  }

  private createNewCoupon(formValue: ActivateFormValue): void {
    let plansSelected = this.fillPlansSelected(formValue);
    let plansIdsList = plansSelected.map(plan => plan.id);
    let randomSequence = Math.random().toString(36).slice(2, 10).toUpperCase();
    let coupon: CouponRequest = {
      id: randomSequence,
      name: `${this.generateCouponName(formValue)} (${randomSequence})`,
      discount_type: DISCOUNT_TYPE.PERCENTAGE,
      discount_percentage: formValue.amount,
      duration_type: formValue.durationForever ? DURATION_TYPE.FOREVER : DURATION_TYPE.LIMITED_PERIOD,
      period: !formValue.durationForever ? formValue.duration : null,
      period_unit: !formValue.durationForever ? 'month' : null,
      valid_till: this.datePipe.transform(calculateExpiryDate(formValue), 'yyyy-MM-dd h:mm:ss'),
      max_redemption: 1, // this must be always 1 to allow customer to use coupon only one time
      chargebee_account: null,
      item_constraints: plansIdsList
    };
    this.loading = true;
    this.activationLinkService.getChargebeeAccountByCountry(formValue.country.iso2.toUpperCase())
      .pipe(
        concatMap((res: any) => {
          coupon.chargebee_account = res.chargebee_account;
          return this.activationLinkService.createNewCoupon(coupon);
        })
      ).subscribe((res: CouponResponse) => {
        this.createLink(formValue, res);
        this.loading = false;
      }, () => {
        this.loading = false;
      });
  }

  private fillPlansSelected(formValue: ActivateFormValue): Plan[] {
    let plansSelected = [];
    if (formValue.plans && formValue.plans.length !== 0) {
      plansSelected = formValue.plans.map(plan => plan.data);
    }
    return plansSelected;
  }

  private generateCouponName(formValue: ActivateFormValue): string {
    let couponName: string;
    switch (formValue.country.iso2.toUpperCase()) {
      case 'IT': {
        couponName = `SCONTO ${formValue.amount}%`;
        break;
      }
      case 'GB':
      case 'US': {
        couponName = `DISCOUNT ${formValue.amount}%`;
        break;
      }
      default: {
        couponName = `DISCOUNT ${formValue.amount}%`;
        break;
      }
    }
    return couponName;
  }

  private createLink(formValue: ActivateFormValue, coupon?): void {
    this.modalService.open(this.activationPlansLinkPopup, { size: 'lg' });
    const expiryDate = calculateExpiryDate(formValue);
    const base64Coupon = btoa(JSON.stringify({
      name: formValue.name,
      date: expiryDate.getTime(),
      deal_id: formValue.deal_id,
      coupon: coupon ? coupon.id : null,
      payment_methods: PAYMENT_METHODS_MAP[formValue.paymentMethod],
      sms: formValue.sms
    }));
    let params = `?token=${base64Coupon}&country=${formValue.country.iso2.toUpperCase()}`
    if (formValue.wifi === 'none') {
      switch (formValue.country.iso2.toUpperCase()) {
        case 'IT':
          this.modalContent = `${environment.activationLink.prefixItalian}${params}`;
          break;
        case 'GB':
        case 'US':
          this.modalContent = `${environment.activationLink.prefixEnglish}${params}`;
          break;
        default:
          this.modalContent = `${environment.activationLink.prefixEnglish}${params}`;
          break;
      }
    } else {
      this.modalContent = formValue.wifi === 'simple'
        ? `${environment.activationLink.withWifiPrefix}${params}`
        : `${environment.activationLink.withWifiFullPrefix}${params}`;
    }
  }
}
